// src/pages/SharePage.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import "../styles/SharePage.css";
import NewPost from "../components/NewPost";
import HeadlineCard from "../components/HeadlineCard";
import { FiFilter, FiSearch, FiHeart, FiTrendingUp } from "react-icons/fi";
import API from "../utils/axiosInstance";
import { useTranslation } from "react-i18next";

export default function SharePage() {
  const { t } = useTranslation("common");
  const [query, setQuery] = useState("");
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  // sort: "date" | "likes"  → API'ye "new" | "top"
  const [sort, setSort] = useState("date");
  const [page, setPage] = useState(1);
  const [next, setNext] = useState(null);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const [role, setRole] = useState(null); // "doctor" | "supplier" | null
  const [isLoggedIn, setIsLoggedIn] = useState(
    typeof window !== "undefined" && localStorage.getItem("isLoggedIn") === "1"
  );

  const sentinelRef = useRef(null);
  const abortRef = useRef(null);

  // --- Kullanıcı rolü ---
  useEffect(() => {
    let didCancel = false;
    async function fetchMe() {
      try {
        const res = await API.get("/api/auth/me/");
        if (!didCancel) {
          setRole(res?.data?.role ?? null);
          setIsLoggedIn(true);
        }
      } catch {
        const fallback = localStorage.getItem("role");
        setRole(fallback || null);
        setIsLoggedIn(localStorage.getItem("isLoggedIn") === "1");
      }
    }
    fetchMe();
    return () => {
      didCancel = true;
    };
  }, []);

  // --- Akış verisi ---
  const fetchFeed = async ({ reset = false, pageArg = null } = {}) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    if (reset) setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query.trim()) params.set("search", query.trim());
      params.set("ordering", sort === "likes" ? "top" : "new");
      params.set("page", String(pageArg ?? (reset ? 1 : page)));

      const res = await API.get(`/api/guncel/posts/?${params.toString()}`, {
        signal: controller.signal,
      });

      const results = res.data?.results ?? res.data;
      const nextUrl = res.data?.next ?? null;
      const normalized = (results || []).map((p) =>
        toHeadlineCard(p, {
          defaultTitle: t("share.fallback.defaultTitle"),
          supplier: t("share.fallback.supplier"),
          user: t("share.fallback.user"),
        })
      );

      if (reset) setFeed(normalized);
      else setFeed((prev) => [...prev, ...normalized]);

      setNext(nextUrl);
    } catch (err) {
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.error("feed error:", err);
      }
    } finally {
      if (reset) setLoading(false);
      setIsLoadingMore(false);
    }
  };

  // Arama / sıralama değiştiğinde ilk sayfa
  useEffect(() => {
    setPage(1);
    fetchFeed({ reset: true, pageArg: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort]);

  // Sonsuz kaydırma
  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;
    const io = new IntersectionObserver(
      (entries) => {
        const [en] = entries;
        if (en.isIntersecting && next && !isLoadingMore && !loading) {
          try {
            const u = new URL(next);
            const nextPage = Number(u.searchParams.get("page") || page + 1);
            setIsLoadingMore(true);
            setPage(nextPage);
            fetchFeed({ reset: false, pageArg: nextPage });
          } catch {
            setIsLoadingMore(true);
            setPage((p) => p + 1);
            fetchFeed({ reset: false, pageArg: page + 1 });
          }
        }
      },
      { rootMargin: "600px 0px 0px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, loading, isLoadingMore]);

  const filtered = useMemo(() => feed, [feed]);

  const i18nFallback = useMemo(
    () => ({
      defaultTitle: t("share.fallback.defaultTitle"),
      supplier: t("share.fallback.supplier"),
      user: t("share.fallback.user"),
    }),
    [t]
  );

  // --- OLUŞTUR: Başlık + Firma + İçerik + Görsel (multipart) ---
  const handleCreatePost = async ({ title, author_company_name, content, imageFile }) => {
    try {
      if (!imageFile) {
        alert(t("share.alert.imageRequired"));
        return;
      }
      await API.get("/api/auth/csrf/").catch(() => {});
      const fd = new FormData();
      fd.append("title", (title || "").trim());
      fd.append("author_company_name", (author_company_name || "").trim());
      fd.append("content", (content || "").trim());
      fd.append("image", imageFile, imageFile.name);

      const res = await API.post("/api/guncel/posts/", fd, {
        /*headers: { "Content-Type": "multipart/form-data" },*/
        /*withCredentials: true,*/
      });

      const created = toHeadlineCard(res.data, i18nFallback);
      setFeed((prev) => [created, ...prev]);
    } catch (err) {
      console.error("create post error:", err);
      alert(t("share.alert.createFailed"));
    }
  };

  // --- DÜZENLE (sadece sahibi) ---
  const handleUpdatePost = async (postId, payload) => {
    try {
      await API.get("/api/auth/csrf/").catch(() => {});

      let body;
      let headers = { "Content-Type": "multipart/form-data" };

      if (payload instanceof FormData) {
        // HeadlineCard’dan direkt FD geldiyse aynen gönder
        body = payload;
      } else {
        // Obje geldiyse FD’ye çevir
        const fd = new FormData();
        const { title, author_company_name, content, imageFile } = payload || {};
        if (title !== undefined) fd.append("title", (title || "").trim());
        if (author_company_name !== undefined)
          fd.append("author_company_name", (author_company_name || "").trim());
        if (content !== undefined) fd.append("content", (content || "").trim());
        if (imageFile) fd.append("image", imageFile, imageFile.name);
        body = fd;
      }

      const res = await API.patch(`/api/guncel/posts/${postId}/`, body, {
        /*headers,*/
        /*withCredentials: true,*/
      });

      // Cache-bust: yeni görsel aynı path ile dönerse dahi tarayıcıyı yenilemek için
      const updated = toHeadlineCard(
        {
          ...res.data,
          image_url:
            (res.data?.image_url || res.data?.image || null)
              ? `${(res.data.image_url || res.data.image)}?t=${Date.now()}`
              : null,
        },
        i18nFallback
      );

      setFeed((prev) => prev.map((p) => (p.id === postId ? updated : p)));
    } catch (err) {
      console.error("update post error:", err);
      alert(t("share.alert.updateFailed"));
    }
  };

  // --- SİL (sadece sahibi) ---
  const handleDeletePost = async (postId) => {
    if (!window.confirm(t("share.confirm.delete"))) return;
    try {
      await API.get("/api/auth/csrf/").catch(() => {});
      await API.delete(`/api/guncel/posts/${postId}/`); /*, { withCredentials: true });*/
      setFeed((prev) => prev.filter((p) => p.id !== postId));
    } catch (err) {
      console.error("delete post error:", err);
      alert(t("share.alert.deleteFailed"));
    }
  };

  // --- Beğeni ---
  const handleToggleLike = async (postId) => {
    try {
      const res = await API.post(`/api/guncel/posts/${postId}/like_toggle/`);
      const { liked, likes_count } = res.data || {};
      setFeed((prev) =>
        prev.map((p) => (p.id === postId ? { ...p, is_liked: liked, likes_count } : p))
      );
      return { liked, likes_count }; // ← HeadlineCard bu değeri kullanacak
    } catch (err) {
      console.error("like toggle error:", err);
      throw err;
    }
  };

  // --- Basit edit akışı (prompt ile) ---
  function openEditPrompt(p) {
    const newTitle = window.prompt(t("share.prompt.title"), p.title || "");
    if (newTitle === null) return;
    const newCompany = window.prompt(t("share.prompt.company"), p.author_company_name || "");
    if (newCompany === null) return;
    const newContent = window.prompt(t("share.prompt.content"), p.content || "");
    if (newContent === null) return;

    handleUpdatePost(p.id, {
      title: newTitle,
      author_company_name: newCompany,
      content: newContent,
      // Görsel değişimi bu hızlı akışta yok; modal ile ekleyebiliriz
    });
  }

  function confirmAndDelete(p) {
    handleDeletePost(p.id);
  }

  return (
    <div className="page share">
      <header className="share__header">
        <div className="share__title-row">
          <h1 className="share__title">
            <FiTrendingUp style={{ marginRight: 10 }} aria-hidden="true" />
            {t("share.title")}
          </h1>

          <div className="share__controls">
            <div className="share__search">
              <FiSearch />
              <input
                type="search"
                placeholder={t("share.search.placeholder")}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label={t("share.search.aria")}
              />
            </div>

            <div className="share__filters" role="group" aria-label={t("share.sort.aria")}>
              <button
                className={`chip ${sort === "date" ? "chip--active" : ""}`}
                onClick={() => setSort("date")}
                title={t("share.sort.dateTitle")}
              >
                <FiFilter style={{ marginRight: 6 }} />
                {t("share.sort.date")}
              </button>
              <button
                className={`chip ${sort === "likes" ? "chip--active" : ""}`}
                onClick={() => setSort("likes")}
                title={t("share.sort.likesTitle")}
              >
                <FiHeart style={{ marginRight: 6 }} />
                {t("share.sort.likes")}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* DUYURU YAZ: yalnız tedarikçi + login */}
      {isLoggedIn && role === "supplier" && (
        <NewPost
          onSubmit={handleCreatePost}
          titleLabel={t("share.newPost.titleLabel")}
          companyLabel={t("share.newPost.companyLabel")}
          contentLabel={t("share.newPost.contentLabel")}
          allowImage={true}
        />
      )}

      {/* Doktor / misafir: sadece okuma */}
      {!isLoggedIn || role === "doctor" ? (
        <div className="share__hint">
          {role === "doctor"
            ? t("share.hint.doctorReadOnly")
            : t("share.hint.loginAsSupplier")}
        </div>
      ) : null}

      <section className="share__feed" aria-live="polite">
        {loading && feed.length === 0 && (
          <div className="share__empty">{t("share.state.loading")}</div>
        )}
        {!loading && filtered.length === 0 && (
          <div className="share__empty">{t("share.state.empty")}</div>
        )}

        {filtered.map((post) => (
          <HeadlineCard
            key={post.id}
            post={post}
            onLike={() => handleToggleLike(post.id)}
            isOwner={post.is_owner === true}
            onEdit={handleUpdatePost}
            onDelete={(p) => confirmAndDelete(p)}
            size="compact"
          />
        ))}

        <div ref={sentinelRef} style={{ height: 1 }} />
        {isLoadingMore && (
          <div className="share__loading-more">{t("share.state.loadingMore")}</div>
        )}
      </section>
    </div>
  );
}

/** Kart için normalize */
function toHeadlineCard(p, i18nFallback = {}) {
  const title = (p?.title || "").trim() || sliceTitle(p?.content || "", 72, i18nFallback);
  const content = (p?.content || "").trim();
  const summary = makeSummary(content);
  const author_display =
    p?.author_company_name?.trim() ||
    (p?.author_role === "supplier"
      ? (i18nFallback.supplier || "Supplier")
      : (i18nFallback.user || "User"));

  return {
    ...p,
    title,
    summary,
    author_display,
    views_count: p?.views_count ?? 0,
    likes_count: p?.likes_count ?? 0,
    is_liked: !!p?.is_liked,
    is_owner: !!p?.is_owner,
    image_url: p?.image_url || p?.image || null,
  };
}

function sliceTitle(text, max = 72, i18nFallback = {}) {
  if (!text) return i18nFallback.defaultTitle || "Announcement";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? clean.slice(0, max).trim() + "…" : clean;
}

function makeSummary(text, max = 220) {
  if (!text) return "";
  const clean = text.replace(/\s+/g, " ").trim();
  return clean.length > max ? clean.slice(0, max).trim() + "…" : clean;
}